import {type UrlRule} from "../types.ts";

import {mapParam} from "./map-param.ts";

/**
 * Extract the given param from the URL in the current param value.
 *
 * This looks at the value of the given param, and if it is a URL or path
 * containing the same param, it will replace the value of the param in the URL
 * being constructed with the value of the param from the param's current value.
 *
 * This is useful for `continue` params, for example, avoiding two redirects.
 *
 * For example, if the param name were `continue`, and the current value of
 * that param were `/login?continue=/home`, or
 * `https://example.com/login?continue=/home`, then this would set the
 * `continue` param to `/home`.
 *
 * @param paramName The name of the param to populate.
 * @returns A URL rule that copies the given param from its own value if that
 * value is a URL containing that param, and sets itself to that embedded
 * value.
 */
export const extractParamFromCurrentValue = (paramName: string): UrlRule =>
    mapParam(paramName, (currentValue) => {
        if (currentValue) {
            try {
                const paramUrl = new URL(currentValue, location.origin);
                const embeddedParamValue = paramUrl.searchParams.get(paramName);
                if (embeddedParamValue) {
                    return embeddedParamValue;
                }
            } catch (e) {
                // The param value is not a URL.
                // Just keep on going.
            }
        }
        return currentValue;
    });
